/**
 * Stand-demo tutorial — walk and orbit scenes, desktop and touch (13/10/2026).
 *
 * Opens the moment the scene is revealed, dead centre, and walks the visitor
 * through the scheme they are on. Each card leaves when the visitor has DONE
 * it, not after a timer; the bubbles stay ~4 s.
 *
 * DESKTOP
 * Walk, Classic:
 *   1  "Click and drag to look around"  a big white hand, black outline,
 *      grabs and drags; no glass, the title in big outlined lettering
 *      → leaves once a real drag has turned the view (~7°+)
 *   2  "WASD or scroll to move"         four keycaps pressed one by one
 *      → leaves on the first W/A/S/D (or arrow / wheel) that moves
 *   3  bubble under the Gamer pill: another way to move — ~4 s
 *   4  bubble under the Scenes button: more demos here — ~4 s
 *   5  bubble over the flythrough ▶ (when the scene has a tour) — ~4 s
 * Walk, Gamer (the Gamer pill, unless the tutorial was skipped):
 *   1  "Click, then move the mouse to look · Esc to let go"  a mouse clicks,
 *      then slides across → leaves once the locked pointer has turned the view
 *   2  the same WASD card
 *   then whichever of 4 and 5 has not been shown yet (3 is Gamer itself).
 * Orbit (Cartoixa) is the same shape with the orbit gestures:
 *   Classic: "Click and drag to rotate" (the hand) → "Scroll to zoom" (a mouse
 *   whose wheel turns) → the three bubbles.
 *   Gamer: "Click, then move the mouse to orbit · Esc to let go" (the mouse
 *   clicks and slides) → "W and S to zoom" (two keycaps) → the bubbles left.
 *
 * TOUCH (iPad, iPhone) — a cartoon glove, white with a black outline, about
 * 70% of the joystick's diameter, does the gesture:
 * Walk, Classic:
 *   1  "Swipe to look around"   the glove's index finger presses (a dotted
 *      ring round the fingertip while it is down), drags across, lifts
 *      → leaves once a swipe has turned the view
 *   2  "Drag the joystick to move"   the pointing glove on a copy of the pad
 *      pushes the knob out and round in a circle → leaves on the first real deflection
 *   3  bubble under the Hold to walk pill — ~4 s, then Scenes and ▶ as above
 * Walk, Hold to walk (the pill):
 *   1  the same swipe card, if it has not run yet
 *   2  "Touch and hold to walk forward"   the index finger presses and stays
 *      down, its dotted ring on → leaves once a hold has started walking
 *   then the bubbles not shown yet.
 * Orbit (Cartoixa, no pills on touch):
 *   1  "Swipe to rotate" (the swipe card)  2  "Pinch to zoom"  the glove,
 *   leaning, presses thumb and index together, spreads them into a V and
 *   lifts, a dotted ring round each tip while down → leaves on the first real pinch; then Scenes and ▶.
 *
 * PHONE (an iPhone: the screen's short side under 600 px). There are no
 * pills and no ▶ at the bottom: a cog in the toolbar opens a menu with the
 * schemes, the flythrough and the quality (ui/demo-menu.ts). The cards are
 * the touch ones above; in place of the pill bubble and the ▶ bubble:
 *   •  bubble under the cog: other controls and the flythrough are in here
 *   •  the first time the menu opens, a guide under it names the options —
 *      Classic, Hold to walk, Flythrough (never the quality: that one is
 *      ours) — and a card that was interrupted by opening the menu comes
 *      back after it.
 *
 * Every piece shows at most once per scene open: a scheme's cards count as
 * seen the moment its flow starts (so coming back to a scheme never replays
 * its animations, even when the visitor switched away mid-card), and a
 * bubble counts as seen once it has been shown. "SKIP TUTORIAL" (terracotta,
 * inside the card) ends it for good — no flow starts after that. A pill with
 * nothing left to show shows nothing: in the demo there is no hint card, on
 * any device. The cards are pointer-transparent so the visitor practises
 * right through them; only the skip button takes clicks and taps.
 */

import { resolveLang, type UiLang } from './lang';

/** What the tutorial reads from the controller the scene built — set-once
 *  flags the controllers raise on real input (never on idle auto-rotation
 *  or easing), reset here at the start of a card so an earlier gesture
 *  cannot pre-pay it. */
export interface TutorialGestures {
    /** [angles…] of the view, radians — the look card wants a real turn. */
    look: () => number[];
    lookArmed: () => boolean;
    resetLook: () => void;
    /** The move card (WASD / wheel / W-S zoom / pinch) leaves on the flag alone. */
    moveArmed: () => boolean;
    resetMove: () => void;
    /** Touch walk only: the joystick card (Classic) and the hold card (Hold
     *  to walk) each leave on their own flag. */
    joystickArmed?: () => boolean;
    resetJoystick?: () => void;
    holdArmed?: () => boolean;
    resetHold?: () => void;
}

export interface DemoTutorialOptions {
    parent: HTMLElement;
    kind: 'walk' | 'orbit';
    /** A touch device: the glove flows instead of the mouse ones. */
    touch?: boolean;
    gestures: TutorialGestures;
    /** Where the bubbles point. A missing anchor skips its bubble. The tour
     *  button is built after the tutorial, hence read when the step runs. */
    anchors: {
        schemes: HTMLElement[];
        scenes: HTMLElement | null;
        tour?: () => HTMLElement | null;
        /** Phone: the cog and the dropdown under it (ui/demo-menu.ts). */
        settings?: HTMLElement | null;
        menu?: HTMLElement | null;
    };
    /** Before the first card of a flow (ms) — lets the reveal land first. */
    delay?: number;
    onEnd?: (how: 'done' | 'skipped') => void;
}

type Piece =
    | 'drag' | 'move' | 'gamerLook' | 'gamerMove'
    | 'swipe' | 'joystick' | 'hold' | 'pinch'
    | 'schemes' | 'settings' | 'menu' | 'scenes' | 'tour';

/** The animated cards, as opposed to the pointing bubbles. */
const CARDS: ReadonlySet<Piece> = new Set<Piece>(['drag', 'move', 'gamerLook', 'gamerMove', 'swipe', 'joystick', 'hold', 'pinch']);

type Registry = 'walk' | 'orbit' | 'walk-touch' | 'orbit-touch';

/** Flows per scene kind and device, keyed by the scheme id
 *  (control-schemes.ts): the walk Gamer is E, the orbit Gamer is G, the
 *  touch walk Hold to walk is C. The swipe card is one piece shared by the
 *  two touch walk schemes: it is the same gesture, taught once. */
const FLOWS: Record<Registry, Record<string, Piece[]>> = {
    walk: {
        A: ['drag', 'move', 'schemes', 'scenes', 'tour'],
        E: ['gamerLook', 'gamerMove', 'scenes', 'tour']
    },
    orbit: {
        A: ['drag', 'move', 'schemes', 'scenes', 'tour'],
        G: ['gamerLook', 'gamerMove', 'scenes', 'tour']
    },
    'walk-touch': {
        A: ['swipe', 'joystick', 'schemes', 'settings', 'scenes', 'tour'],
        C: ['swipe', 'hold', 'scenes', 'tour']
    },
    'orbit-touch': {
        A: ['swipe', 'pinch', 'settings', 'scenes', 'tour']
    }
};

type Copy = {
    drag: string; move: string; gamerLook: string; gamerMove: string;
    orbitDrag: string; orbitMove: string; orbitGamerLook: string; orbitGamerMove: string;
    swipe: string; joystick: string; hold: string; orbitSwipe: string; pinch: string;
    schemes: string; schemesTouch: string; scenes: string; tour: string; skip: string;
    settings: string; settingsOrbit: string; menuGuide: string; menuGuideOrbit: string;
};
const COPY: Record<UiLang, Copy> = {
    en: {
        drag: 'Click and drag to look around',
        move: 'WASD or scroll to move',
        gamerLook: 'Click, then move the mouse to look · Esc to let go',
        gamerMove: 'WASD or scroll to move',
        orbitDrag: 'Click and drag to rotate',
        orbitMove: 'Scroll to zoom',
        orbitGamerLook: 'Click, then move the mouse to orbit · Esc to let go',
        orbitGamerMove: 'W and S to zoom',
        swipe: 'Swipe to look around',
        joystick: 'Drag the joystick to move',
        hold: 'Touch and hold to walk forward',
        orbitSwipe: 'Swipe to rotate',
        pinch: 'Pinch to zoom',
        schemes: 'Gamer is another way to move, with its own controls',
        schemesTouch: 'Hold to walk is another way to move',
        scenes: 'More demos here',
        tour: 'Try the flythrough',
        skip: 'Skip tutorial',
        settings: 'Other controls and the flythrough are in here',
        settingsOrbit: 'The flythrough is in here',
        menuGuide: 'Classic: swipe to look, drag the joystick to move\nHold to walk: hold the screen to walk forward\nFlythrough: a guided tour of the scene',
        menuGuideOrbit: 'Flythrough: a guided tour of the scene'
    },
    es: {
        drag: 'Haz clic y arrastra para mirar alrededor',
        move: 'WASD o scroll para moverte',
        gamerLook: 'Haz clic y mueve el ratón para mirar · Esc para soltar',
        gamerMove: 'WASD o scroll para moverte',
        orbitDrag: 'Haz clic y arrastra para girar',
        orbitMove: 'Scroll para acercar o alejar',
        orbitGamerLook: 'Haz clic y mueve el ratón para girar · Esc para soltar',
        orbitGamerMove: 'W y S para acercar o alejar',
        swipe: 'Desliza para mirar alrededor',
        joystick: 'Arrastra el joystick para moverte',
        hold: 'Mantén el dedo en la pantalla para avanzar',
        orbitSwipe: 'Desliza para girar',
        pinch: 'Pellizca para acercar o alejar',
        schemes: 'Gamer es otra forma de moverte, con sus propios controles',
        schemesTouch: 'Mantén para andar es otra forma de moverte',
        scenes: 'Más demos aquí',
        tour: 'Prueba el recorrido',
        skip: 'Saltar tutorial',
        settings: 'Aquí tienes otros controles y el recorrido',
        settingsOrbit: 'Aquí tienes el recorrido',
        menuGuide: 'Clásico: desliza para mirar, arrastra el joystick para moverte\nMantén para andar: mantén el dedo en la pantalla para avanzar\nRecorrido: una visita guiada por la escena',
        menuGuideOrbit: 'Recorrido: una visita guiada por la escena'
    },
    ca: {
        drag: 'Fes clic i arrossega per mirar al voltant',
        move: 'WASD o scroll per moure’t',
        gamerLook: 'Fes clic i mou el ratolí per mirar · Esc per deixar anar',
        gamerMove: 'WASD o scroll per moure’t',
        orbitDrag: 'Fes clic i arrossega per girar',
        orbitMove: 'Scroll per apropar o allunyar',
        orbitGamerLook: 'Fes clic i mou el ratolí per girar · Esc per deixar anar',
        orbitGamerMove: 'W i S per apropar o allunyar',
        swipe: 'Llisca per mirar al voltant',
        joystick: 'Arrossega el joystick per moure’t',
        hold: 'Mantén el dit a la pantalla per avançar',
        orbitSwipe: 'Llisca per girar',
        pinch: 'Pessiga per apropar o allunyar',
        schemes: 'Gamer és una altra manera de moure’t, amb els seus propis controls',
        schemesTouch: 'Mantén per caminar és una altra manera de moure’t',
        scenes: 'Més demos aquí',
        tour: 'Prova el recorregut',
        skip: 'Omet el tutorial',
        settings: 'Aquí tens altres controls i el recorregut',
        settingsOrbit: 'Aquí tens el recorregut',
        menuGuide: 'Clàssic: llisca per mirar, arrossega el joystick per moure’t\nMantén per caminar: mantén el dit a la pantalla per avançar\nRecorregut: una visita guiada per l’escena',
        menuGuideOrbit: 'Recorregut: una visita guiada per l’escena'
    }
};

/** A drag has to turn the view this much (radians, ~7°) to count. */
const LOOK_THRESHOLD = 0.12;
/** How long the pointing bubbles stay (ms); the menu guide, three lines, longer. */
const BUBBLE_MS = 4000;
const GUIDE_MS = 9000;
/** Fade-out length; must match the CSS transition. */
const FADE_MS = 350;

// The Gamer mouse for the bare card (20/10/2026): the drag glove's white
// and black, its left button lighting up light terracotta on the click.
// Body fill, then the button, then the outline and the split on top.
const MOUSE_BIG = `<svg class="ollo-tut-glove" viewBox="0 0 24 24" aria-hidden="true">
    <rect class="ollo-tut-mbody" x="6" y="2.5" width="12" height="19" rx="6"/>
    <path class="ollo-tut-mouse-btn" d="M12 2.5A6 6 0 0 0 6 8.5V10h6z"/>
    <rect class="ollo-tut-mline" x="6" y="2.5" width="12" height="19" rx="6"/>
    <path class="ollo-tut-mline" d="M12 2.5V10M6 10h12"/>
</svg>`;
// The mouse with its wheel, for the bare Cartoixa card (20/10/2026): the
// Gamer mouse's white and black, the wheel a light terracotta pill whose
// dashed edge runs (stroke-dashoffset) so it reads as turning.
const MOUSE_WHEEL = `<svg class="ollo-tut-glove" viewBox="0 0 24 24" aria-hidden="true">
    <rect class="ollo-tut-mbody" x="6" y="2.5" width="12" height="19" rx="6"/>
    <rect class="ollo-tut-mline" x="6" y="2.5" width="12" height="19" rx="6"/>
    <path class="ollo-tut-mline" d="M12 2.5V4.2M12 9.3V10M6 10h12"/>
    <rect class="ollo-tut-mouse-wheel" x="10.6" y="4.2" width="2.8" height="5.1" rx="1.4"/>
</svg>`;

// ── The glove (touch) ───────────────────────────────────────────────────
// A cartoon glove in the old-cartoon manner: white, a black outline, a cuff
// at the wrist and three lines on the back. Built from capsules and circles
// drawn TWICE — first every shape with a thick black stroke, then every
// shape again as a white fill on top — so the parts fuse into one outlined
// silhouette with no seam where a finger meets the palm. Thin lines go on
// top for the cuff edge and the back of the hand. Coordinates are in a
// 64 × 64 box.
const glove = (shapes: string, lines = ''): string =>
    `<g class="ollo-tut-ol">${shapes}</g><g class="ollo-tut-fi">${shapes}</g>` +
    (lines ? `<g class="ollo-tut-ln">${lines}</g>` : '');

// The desktop drag hand (20/10/2026, Lenny: the glove's white and black,
// much bigger): an open hand, fingers up and the thumb out, that closes
// into a grab — short knuckles, the thumb folded in. Same cuff as the glove.
const DRAG_CUFF = `<rect x="17" y="50" width="31" height="9" rx="3.5"/>`;
const OPEN_SHAPES =
    `<rect x="18" y="12" width="8" height="28" rx="4"/><rect x="26" y="7" width="8" height="33" rx="4"/>` +
    `<rect x="34" y="10" width="8" height="30" rx="4"/><rect x="42" y="17" width="7" height="24" rx="3.5"/>` +
    `<rect x="12" y="28" width="9" height="22" rx="4.5" transform="rotate(-34 20 47)"/>` +
    `<rect x="17" y="28" width="32" height="24" rx="9"/>` + DRAG_CUFF;
const GRAB_SHAPES =
    `<rect x="18" y="22" width="8" height="18" rx="4"/><rect x="26" y="20" width="8" height="20" rx="4"/>` +
    `<rect x="34" y="21" width="8" height="19" rx="4"/><rect x="42" y="24" width="7" height="17" rx="3.5"/>` +
    `<rect x="13" y="32" width="9" height="17" rx="4.5" transform="rotate(-58 19 45)"/>` +
    `<rect x="17" y="28" width="32" height="24" rx="9"/>` + DRAG_CUFF;
const HAND_OPEN = `<svg class="ollo-tut-glove" viewBox="0 0 64 64" aria-hidden="true">` +
    glove(OPEN_SHAPES, `<path d="M26 18V32M34 16V32M42 22V33M19 50.5h27"/>`) + `</svg>`;
const HAND_GRAB = `<svg class="ollo-tut-glove" viewBox="0 0 64 64" aria-hidden="true">` +
    glove(GRAB_SHAPES, `<path d="M26 24V30M34 23V30M42 26V31M19 50.5h27"/>`) + `</svg>`;

// Design 9 of documentation/viewer/hand-icons (Lenny's pick, 15/10/2026):
// one fused silhouette — index up, two folded fingers, the thumb tucked
// along the left — a cuff band, two knuckle lines and a dashed ripple ring
// round the fingertip. Tilted 8° about the fingertip, which stays at
// (28, 7): the touch halo is placed there and the press scales around it.
const POINT_SHAPES =
    `<path d="M22 32 V11 a6 6 0 0 1 12 0 V27.5 a4.6 4.6 0 0 1 8.8 1.2 a4.4 4.4 0 0 1 8 2.6 ` +
    `V44 a6 6 0 0 1 -6 6 H21 a6 6 0 0 1 -6 -6 V39 a5.2 5.2 0 0 1 4 -9.5 Z"/>` +
    `<path d="M18 50 h28 a3.5 3.5 0 0 1 3.5 3.5 v1.5 a3.5 3.5 0 0 1 -3.5 3.5 h-28 ` +
    `a3.5 3.5 0 0 1 -3.5 -3.5 v-1.5 a3.5 3.5 0 0 1 3.5 -3.5 z"/>`;
const POINT_LINES = `<path d="M37.5 35 v7 M44.5 36.5 v6"/>`;
const POINT_RING = `<g class="ollo-tut-touch"><circle class="ollo-tut-ripple-ol" cx="28" cy="7" r="9"/><circle class="ollo-tut-ripple" cx="28" cy="7" r="9"/></g>`;
const HAND_POINT = `<svg class="ollo-tut-glove" viewBox="0 0 64 64" aria-hidden="true">` +
    `<g transform="rotate(-8 28 7)">${POINT_RING}${glove(POINT_SHAPES, POINT_LINES)}</g></svg>`;

// The pinch (Lenny's pick from public/pinch-icons.html, round 2 design 1,
// 15/10/2026): design 9's fist with the index up from its knuckle (28, 32)
// and the thumb out to the left from (20, 41), all one fused silhouette —
// no line where the thumb meets the hand. The whole hand leans 28° so the
// spread runs along the diagonal. CSS turns the index and the thumb shut
// (tips together) and open (a V); a halo under each tip while it touches.
const PINCH_FIST =
    `<path d="M22 31 a6 6 0 0 1 12 -2 V27.5 a4.6 4.6 0 0 1 8.8 1.2 a4.4 4.4 0 0 1 8 2.6 V44 ` +
    `a6 6 0 0 1 -6 6 H21 a6 6 0 0 1 -6 -6 V37 a6 6 0 0 1 7 -6 Z"/>`;
const PINCH_CUFF =
    `<path d="M18 50 h28 a3.5 3.5 0 0 1 3.5 3.5 v1.5 a3.5 3.5 0 0 1 -3.5 3.5 h-28 ` +
    `a3.5 3.5 0 0 1 -3.5 -3.5 v-1.5 a3.5 3.5 0 0 1 3.5 -3.5 z"/>`;
const PINCH_SHAPES =
    `<g class="ollo-tut-thb"><rect x="14.75" y="21" width="10.5" height="25.25" rx="5.25" transform="rotate(-62 20 41)"/></g>` +
    PINCH_CUFF + PINCH_FIST +
    `<g class="ollo-tut-idx"><rect x="22.25" y="8" width="11.5" height="29.75" rx="5.75"/></g>`;
const PINCH_LINES = `<path d="M37.5 35 v7 M44.5 36.5 v6"/>`;
const PINCH_HALOS =
    `<g class="ollo-tut-thb"><g class="ollo-tut-touch"><circle class="ollo-tut-ripple-ol" cx="2.34" cy="31.61" r="8"/><circle class="ollo-tut-ripple" cx="2.34" cy="31.61" r="8"/></g></g>` +
    `<g class="ollo-tut-idx"><g class="ollo-tut-touch"><circle class="ollo-tut-ripple-ol" cx="28" cy="8" r="8"/><circle class="ollo-tut-ripple" cx="28" cy="8" r="8"/></g></g>`;
const HAND_PINCH = `<svg class="ollo-tut-glove" viewBox="0 0 64 64" aria-hidden="true">` +
    `<g transform="rotate(28 32 40)"><g class="ollo-tut-pinch-press">${PINCH_HALOS}${glove(PINCH_SHAPES, PINCH_LINES)}</g></g></svg>`;

/** The pointing glove, for the swipe and hold cards. Its dotted ring shows
 *  only while the finger is down (Lenny, 20/10/2026: no white halo). */
const FINGER = `<span class="ollo-tut-finger">${HAND_POINT}</span>`;

export class DemoTutorial {

    private root: HTMLDivElement;

    private t: Copy;

    private current: HTMLElement | null = null;

    private timer: number | null = null;

    private poll: number | null = null;

    private onResize: (() => void) | null = null;

    /** Pieces that have run (or started) this scene open — never again. */
    private seen = new Set<Piece>();

    /** The flow in progress, pieces still to run. */
    private queue: Piece[] = [];

    /** The piece on screen (null between pieces). */
    private currentPiece: Piece | null = null;

    private skipped = false;

    private disposed = false;

    private registry: Registry;

    constructor(private opts: DemoTutorialOptions) {
        this.t = COPY[resolveLang()];
        this.registry = opts.touch ? (opts.kind === 'walk' ? 'walk-touch' : 'orbit-touch') : opts.kind;
        this.root = document.createElement('div');
        this.root.className = 'ollo-tut';
        opts.parent.appendChild(this.root);
    }

    /**
     * Begin the flow for a scheme, dropping whatever piece is up. False when
     * nothing will show — skipped earlier, no flow for this scheme, or every
     * piece of it already ran.
     */
    start(scheme: string): boolean {
        if (this.disposed || this.skipped) return false;
        // Whatever is up teaches the scheme just left: down with it, even
        // when the new scheme brings nothing of its own.
        this.clearWatchers();
        this.hideCurrent();
        this.queue = [];
        const flows = FLOWS[this.registry];
        const flow = flows[scheme];
        if (!flow) return false;
        // Picking Gamer (or Hold to walk) IS the "another way" bubble — and
        // on a phone, proof the visitor found the cog.
        if (scheme !== 'A') { this.seen.add('schemes'); this.seen.add('settings'); }
        const pending = flow.filter(p => !this.seen.has(p));
        if (pending.length === 0) return false;
        // Picked: this scheme's cards are spent, shown now or not.
        for (const p of flow) if (CARDS.has(p)) this.seen.add(p);
        this.queue = pending;
        this.timer = window.setTimeout(() => this.runNext(), this.opts.delay ?? 700);
        return true;
    }

    // ── Flow ────────────────────────────────────────────────────────────

    private runNext(): void {
        if (this.disposed || this.skipped) return;
        const piece = this.queue.shift();
        this.currentPiece = null;
        if (!piece) { this.opts.onEnd?.('done'); return; }
        // A bubble already shown by the other scheme's flow in the meantime.
        if (!CARDS.has(piece) && this.seen.has(piece)) { this.runNext(); return; }
        this.seen.add(piece);
        this.currentPiece = piece;
        const orbit = this.opts.kind === 'orbit';
        switch (piece) {
            case 'drag': this.stepDrag(orbit ? this.t.orbitDrag : this.t.drag); break;
            case 'move': orbit ? this.stepScroll(this.t.orbitMove) : this.stepKeys(this.t.move, ['W', 'A', 'S', 'D']); break;
            case 'gamerLook': this.stepGamerLook(orbit ? this.t.orbitGamerLook : this.t.gamerLook); break;
            case 'gamerMove': orbit ? this.stepKeys(this.t.orbitGamerMove, ['W', 'S']) : this.stepKeys(this.t.gamerMove, ['W', 'A', 'S', 'D']); break;
            case 'swipe': this.stepSwipe(orbit ? this.t.orbitSwipe : this.t.swipe); break;
            case 'joystick': this.stepJoystick(this.t.joystick); break;
            case 'hold': this.stepHold(this.t.hold); break;
            case 'pinch': this.stepPinch(this.t.pinch); break;
            case 'schemes': this.stepSchemes(); break;
            case 'settings': this.stepSettings(); break;
            case 'menu': this.runNext(); break; // driven by menuOpened(), never queued
            case 'scenes': this.stepScenes(); break;
            case 'tour': this.stepTour(); break;
        }
    }

    // ── Steps ───────────────────────────────────────────────────────────

    private stepDrag(title: string): void {
        const card = this.card(
            `<div class="ollo-tut-drag big">
                <span class="ollo-tut-hand"><span class="ollo-tut-hand-open">${HAND_OPEN}</span><span class="ollo-tut-hand-grab">${HAND_GRAB}</span></span>
            </div>`,
            title
        );
        // No glass: big lettering straight on the scene (Lenny, 20/10/2026).
        card.classList.add('bare');
        this.show(card);
        this.watchLook();
    }

    private stepGamerLook(title: string): void {
        const card = this.card(
            `<div class="ollo-tut-drag big">
                <span class="ollo-tut-mouse">${MOUSE_BIG}</span>
            </div>`,
            // "Esc to let go" on a line of its own, not wrapped after the dot.
            title.replace(' · ', '\n')
        );
        card.classList.add('bare');
        this.show(card);
        this.watchLook();
    }

    /** Touch: the glove presses, drags along the track, lifts. */
    private stepSwipe(title: string): void {
        const card = this.card(`<div class="ollo-tut-swipe">${FINGER}</div>`, title);
        card.classList.add('bare');
        this.show(card);
        this.watchLook();
    }

    /** Touch, Hold to walk: the glove presses and stays down. */
    private stepHold(title: string): void {
        const card = this.card(`<div class="ollo-tut-hold">${FINGER}</div>`, title);
        card.classList.add('bare');
        this.show(card);
        const g = this.opts.gestures;
        this.watchFlag(() => g.holdArmed?.() ?? false, () => g.resetHold?.());
    }

    /** Touch, Classic: the pointing glove (the swipe card's) pushes the knob
     *  of a copy of the pad out and round. Knob and glove ride one arm that
     *  turns; the glove turns back the same amount so it stays upright. */
    private stepJoystick(title: string): void {
        const card = this.card(
            `<div class="ollo-tut-joy"><span class="ollo-tut-joy-ring"></span>
                <span class="ollo-tut-joy-orbit"><span class="ollo-tut-joy-arm">
                    <span class="ollo-tut-joy-knob"></span>
                    <span class="ollo-tut-joy-hand">${HAND_POINT}</span>
                </span></span>
            </div>`,
            title
        );
        card.classList.add('bare');
        this.show(card);
        const g = this.opts.gestures;
        this.watchFlag(() => g.joystickArmed?.() ?? false, () => g.resetJoystick?.());
    }

    /** Touch orbit: thumb and index touch down together and spread. */
    private stepPinch(title: string): void {
        const card = this.card(`<div class="ollo-tut-pinch"><span class="ollo-tut-pinch-hand">${HAND_PINCH}</span></div>`, title);
        card.classList.add('bare');
        this.show(card);
        this.watchMove();
    }

    /** Leaves once the controller saw a look gesture AND the view turned. */
    private watchLook(): void {
        const g = this.opts.gestures;
        // Set-once flag: a drag during an earlier card must not pre-pay this
        // one — and under Gamer, the look that counts is the locked one.
        g.resetLook();
        let base = g.look();
        this.poll = window.setInterval(() => {
            const now = g.look();
            // Not looked yet: whatever moved the camera (a click-glide, a
            // key, the orbit's idle turn) doesn't count, so re-base until the
            // real gesture starts.
            if (!g.lookArmed()) { base = now; return; }
            const d = Math.hypot(...now.map((v, k) => v - (base[k] ?? 0)));
            if (d > LOOK_THRESHOLD) this.next(() => this.runNext());
        }, 150);
    }

    /** Keycaps pressed one by one; leaves on the first real press. */
    private stepKeys(title: string, keys: string[]): void {
        // Two keys (W, S) press 1.4 s apart; four press 0.7 s apart.
        const gap = keys.length > 2 ? 1 : 2;
        const caps = keys.map((k, i) =>
            `<kbd class="ollo-tut-key" style="--i:${i * gap}" data-k="${k.toLowerCase()}">${k}</kbd>`).join('');
        const card = this.card(`<div class="ollo-tut-keys">${caps}</div>`, title);
        // Matches the drag card: no glass, big keys (Lenny, 20/10/2026).
        card.classList.add('bare');
        this.show(card);
        this.watchMove();
    }

    /** The mouse, still, its wheel turning; leaves on the first real wheel. */
    private stepScroll(title: string): void {
        const card = this.card(`<div class="ollo-tut-scroll big"><span class="ollo-tut-mouse still">${MOUSE_WHEEL}</span></div>`, title);
        card.classList.add('bare');
        this.show(card);
        this.watchMove();
    }

    private watchMove(): void {
        const g = this.opts.gestures;
        this.watchFlag(() => g.moveArmed(), () => g.resetMove());
    }

    /** Leaves once a set-once controller flag goes up. Reset first: a
     *  gesture made during the card before must not pre-pay this one. A
     *  held key keeps sending keydown (auto-repeat), so a visitor already
     *  walking passes right away. */
    private watchFlag(armed: () => boolean, reset: () => void): void {
        reset();
        this.poll = window.setInterval(() => {
            if (armed()) this.next(() => this.runNext());
        }, 100);
    }

    private stepSchemes(): void {
        const anchors = this.opts.anchors.schemes.filter(Boolean);
        if (!anchors.length) { this.runNext(); return; }
        this.bubble(this.opts.touch ? this.t.schemesTouch : this.t.schemes, anchors, () => this.runNext());
    }

    private stepSettings(): void {
        const cog = this.opts.anchors.settings ?? null;
        if (!cog) { this.runNext(); return; }
        this.bubble(this.opts.kind === 'orbit' ? this.t.settingsOrbit : this.t.settings, [cog], () => this.runNext());
    }

    /**
     * Phone: the menu just opened. The first time, a guide under it names
     * the options. Whatever was up comes down; a card comes back afterwards
     * (its gesture is still untaught), a bubble does not.
     */
    menuOpened(): void {
        if (this.disposed || this.skipped || this.seen.has('menu')) return;
        const panel = this.opts.anchors.menu ?? null;
        if (!panel) return;
        this.seen.add('menu');
        this.seen.add('settings');
        if (this.currentPiece && CARDS.has(this.currentPiece)) this.queue.unshift(this.currentPiece);
        this.clearWatchers();
        this.hideCurrent();
        this.currentPiece = 'menu';
        const guide = this.opts.kind === 'orbit' || !this.opts.anchors.schemes.length && this.opts.kind !== 'walk'
            ? this.t.menuGuideOrbit : this.t.menuGuide;
        this.bubble(guide, [panel], () => this.runNext(), 'below', GUIDE_MS);
    }

    /** Phone: the menu closed — a guide still up goes with it. */
    menuClosed(): void {
        if (this.currentPiece === 'menu') this.next(() => this.runNext());
    }

    private stepScenes(): void {
        const btn = this.opts.anchors.scenes;
        if (!btn) { this.runNext(); return; }
        this.bubble(this.t.scenes, [btn], () => this.runNext());
    }

    private stepTour(): void {
        const btn = this.opts.anchors.tour?.() ?? null;
        if (!btn) { this.runNext(); return; }
        this.bubble(this.t.tour, [btn], () => this.runNext(), 'above');
    }

    // ── Pieces ──────────────────────────────────────────────────────────

    /** The glass card: figure, title and, inside it, the skip button. */
    private card(figure: string, title: string): HTMLElement {
        const el = document.createElement('div');
        el.className = 'ollo-tut-card';
        el.setAttribute('role', 'note');
        el.innerHTML = `<div class="ollo-tut-figure">${figure}</div><p class="ollo-tut-title"></p>`;
        el.querySelector('.ollo-tut-title')!.textContent = title;
        const skip = document.createElement('button');
        skip.type = 'button';
        skip.className = 'ollo-tut-skip';
        skip.textContent = this.t.skip;
        skip.addEventListener('click', (e) => { e.stopPropagation(); this.end('skipped'); });
        el.appendChild(skip);
        return el;
    }

    private bubble(text: string, anchors: HTMLElement[], after: () => void, side: 'below' | 'above' = 'below', ms = BUBBLE_MS): void {
        const el = document.createElement('div');
        el.className = side === 'above' ? 'ollo-tut-bubble above' : 'ollo-tut-bubble';
        if (text.includes('\n')) el.classList.add('guide');
        el.setAttribute('role', 'note');
        el.textContent = text;
        this.show(el);
        const place = () => this.place(el, anchors, side);
        place();
        this.onResize = place;
        window.addEventListener('resize', place);
        this.timer = window.setTimeout(() => this.next(after), ms);
    }

    /** Under (or over) the anchors' joint box, arrow on their centre, kept
     *  on screen. */
    private place(el: HTMLElement, anchors: HTMLElement[], side: 'below' | 'above'): void {
        const host = this.opts.parent.getBoundingClientRect();
        const rects = anchors.map(a => a.getBoundingClientRect());
        const left = Math.min(...rects.map(r => r.left));
        const right = Math.max(...rects.map(r => r.right));
        const bottom = Math.max(...rects.map(r => r.bottom));
        const top = Math.min(...rects.map(r => r.top));
        const cx = (left + right) / 2 - host.left;
        const w = el.offsetWidth;
        const margin = 12;
        const x = Math.max(margin, Math.min(cx - w / 2, host.width - w - margin));
        el.style.left = `${Math.round(x)}px`;
        el.style.top = side === 'above'
            ? `${Math.round(top - host.top - 12 - el.offsetHeight)}px`
            : `${Math.round(bottom - host.top + 12)}px`;
        el.style.setProperty('--arrow-x', `${Math.round(cx - x)}px`);
    }

    private show(el: HTMLElement): void {
        this.root.appendChild(el);
        this.current = el;
        // Two frames so the transition runs from the hidden state.
        requestAnimationFrame(() => requestAnimationFrame(() => {
            if (this.current === el) el.classList.add('visible');
        }));
    }

    /** Fade the current piece out; it is removed after the transition. */
    private hideCurrent(): HTMLElement | null {
        const el = this.current;
        this.current = null;
        if (el) {
            el.classList.remove('visible');
            window.setTimeout(() => el.remove(), FADE_MS);
        }
        return el;
    }

    /** Fade the current piece out, then run the next step. */
    private next(then: () => void): void {
        if (this.disposed || this.skipped) return;
        this.clearWatchers();
        const el = this.hideCurrent();
        this.timer = window.setTimeout(() => { if (!this.disposed && !this.skipped) then(); }, el ? FADE_MS * 0.6 : 0);
    }

    private clearWatchers(): void {
        if (this.timer !== null) { clearTimeout(this.timer); this.timer = null; }
        if (this.poll !== null) { clearInterval(this.poll); this.poll = null; }
        if (this.onResize) { window.removeEventListener('resize', this.onResize); this.onResize = null; }
    }

    /** Skip: the tutorial is over for this scene open, every scheme's flow. */
    private end(how: 'done' | 'skipped'): void {
        if (this.disposed || this.skipped) return;
        this.skipped = true;
        this.queue = [];
        this.clearWatchers();
        this.hideCurrent();
        this.opts.onEnd?.(how);
    }

    /** Pull everything out at once (viewer closing). */
    dispose(): void {
        this.disposed = true;
        this.clearWatchers();
        this.root.remove();
    }
}
